import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getAllRecords } from '../services/api';

function HistoryTimeline({ section, patientId }) {
  const [odRecords, setOdRecords] = useState([]);
  const [osRecords, setOsRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    OD: true,
    OS: true
  });

  useEffect(() => {
    loadHistory();
  }, [section, patientId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const [odData, osData] = await Promise.all([
        getAllRecords(section, patientId, 'od'),
        getAllRecords(section, patientId, 'os'),
      ]);

      // Sort by created_at descending
      const sortedOd = odData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const sortedOs = osData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setOdRecords(sortedOd);
      setOsRecords(sortedOs);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (recordId) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [recordId]: !prev[recordId],
    }));
  };

  const toggleSection = (eyeLabel) => {
    setExpandedSections((prev) => ({
      ...prev,
      [eyeLabel]: !prev[eyeLabel],
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined || value === '') return null;
    return `${key}: ${value}`;
  };

  const formatRecordData = (record) => {
    const data = record.data || {};
    const fields = [];
    
    switch (section) {
      case 'acuity':
        if (data.unaided) fields.push(formatFieldValue('Unaided', data.unaided));
        if (data.pinhole) fields.push(formatFieldValue('Pinhole', data.pinhole));
        if (data.bcva) fields.push(formatFieldValue('BCVA', data.bcva));
        if (data.near) fields.push(formatFieldValue('Near', data.near));
        if (data.method) fields.push(formatFieldValue('Method', data.method));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
      case 'refraction':
        if (data.obj_sph != null) fields.push(formatFieldValue('Obj Sph', data.obj_sph));
        if (data.obj_cyl != null) fields.push(formatFieldValue('Obj Cyl', data.obj_cyl));
        if (data.obj_axis != null) fields.push(formatFieldValue('Obj Axis', data.obj_axis));
        if (data.sub_sph != null) fields.push(formatFieldValue('Sub Sph', data.sub_sph));
        if (data.sub_cyl != null) fields.push(formatFieldValue('Sub Cyl', data.sub_cyl));
        if (data.sub_axis != null) fields.push(formatFieldValue('Sub Axis', data.sub_axis));
        if (data.add != null) fields.push(formatFieldValue('Add', data.add));
        if (data.pd != null) fields.push(formatFieldValue('PD', data.pd));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
      case 'iop':
        if (data.value != null) fields.push(formatFieldValue('IOP', data.value + ' mmHg'));
        if (data.method) fields.push(formatFieldValue('Method', data.method));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
      case 'slitlamp':
        if (data.lids) fields.push(formatFieldValue('Lids', data.lids));
        if (data.conjunctiva) fields.push(formatFieldValue('Conjunctiva', data.conjunctiva));
        if (data.cornea) fields.push(formatFieldValue('Cornea', data.cornea));
        if (data.ac) fields.push(formatFieldValue('AC', data.ac));
        if (data.iris) fields.push(formatFieldValue('Iris', data.iris));
        if (data.lens) fields.push(formatFieldValue('Lens', data.lens));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
      case 'fundus':
        if (data.optic_disc) fields.push(formatFieldValue('Optic Disc', data.optic_disc));
        if (data.macula) fields.push(formatFieldValue('Macula', data.macula));
        if (data.vessels) fields.push(formatFieldValue('Vessels', data.vessels));
        if (data.periphery) fields.push(formatFieldValue('Periphery', data.periphery));
        if (data.cdr != null) fields.push(formatFieldValue('C/D Ratio', data.cdr));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
      case 'vf':
        if (data.test_type) fields.push(formatFieldValue('Test Type', data.test_type));
        if (data.md != null) fields.push(formatFieldValue('MD', data.md + ' dB'));
        if (data.psd != null) fields.push(formatFieldValue('PSD', data.psd + ' dB'));
        if (data.vfi != null) fields.push(formatFieldValue('VFI', data.vfi + '%'));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
      case 'keratometry':
        if (data.k1 != null) fields.push(formatFieldValue('K1', data.k1 + ' D'));
        if (data.k2 != null) fields.push(formatFieldValue('K2', data.k2 + ' D'));
        if (data.k_axis != null) fields.push(formatFieldValue('K Axis', data.k_axis + '°'));
        if (data.pachymetry != null) fields.push(formatFieldValue('Pachymetry', data.pachymetry + ' µm'));
        if (data.axial_length != null) fields.push(formatFieldValue('Axial Length', data.axial_length + ' mm'));
        if (data.acd != null) fields.push(formatFieldValue('ACD', data.acd + ' mm'));
        if (data.lens_thickness != null) fields.push(formatFieldValue('Lens Thickness', data.lens_thickness + ' mm'));
        if (data.iol_power != null) fields.push(formatFieldValue('IOL Power', data.iol_power + ' D'));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
      case 'gonioscopy':
        if (data.sup != null) fields.push(formatFieldValue('Superior', data.sup));
        if (data.inf != null) fields.push(formatFieldValue('Inferior', data.inf));
        if (data.nasal != null) fields.push(formatFieldValue('Nasal', data.nasal));
        if (data.temporal != null) fields.push(formatFieldValue('Temporal', data.temporal));
        if (data.overall) fields.push(formatFieldValue('Overall', data.overall));
        if (data.notes) fields.push(formatFieldValue('Notes', data.notes));
        break;
    }
    
    return fields.filter(f => f !== null);
  };

  const renderEyeHistory = (eyeLabel, records) => {
    const isSectionExpanded = expandedSections[eyeLabel];
    
    if (loading) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection(eyeLabel)}
          >
            <h5 className="font-semibold text-[#1a3a73]">{eyeLabel}</h5>
            {isSectionExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-600" />
            )}
          </div>
          {isSectionExpanded && (
            <div className="text-gray-500 mt-2">Loading...</div>
          )}
        </div>
      );
    }

    if (!records || records.length === 0) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection(eyeLabel)}
          >
            <h5 className="font-semibold text-[#1a3a73]">{eyeLabel}</h5>
            {isSectionExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-600" />
            )}
          </div>
          {isSectionExpanded && (
            <div className="text-gray-500 py-2 mt-2">No entries</div>
          )}
        </div>
      );
    }

    return (
      <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
        <div 
          className="flex items-center justify-between cursor-pointer mb-3 hover:bg-gray-100 p-2 rounded -m-2"
          onClick={() => toggleSection(eyeLabel)}
        >
          <h5 className="font-semibold text-[#1a3a73]">{eyeLabel} ({records.length} {records.length === 1 ? 'entry' : 'entries'})</h5>
          {isSectionExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-600" />
          )}
        </div>
        {isSectionExpanded && (
          <div className="space-y-2">
            {records.map((record) => {
              const recordId = `${eyeLabel}-${record.id}`;
              const isExpanded = expandedRecords[recordId];
              const formattedFields = formatRecordData(record);
              return (
                <div
                  key={record.id}
                  className="border-b border-dashed border-gray-300 pb-2 last:border-b-0 cursor-pointer hover:bg-gray-100 p-2 rounded"
                  onClick={() => toggleExpand(recordId)}
                >
                  <div className="text-sm text-gray-600 mb-2">
                    {formatDate(record.created_at)}
                  </div>
                  {formattedFields.length > 0 ? (
                    <div className="space-y-1">
                      {formattedFields.map((field, idx) => (
                        <div key={idx} className="text-sm text-gray-800">{field}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">No data</div>
                  )}
                  {isExpanded && (
                    <div className="mt-2 p-2 bg-white rounded border border-gray-200 font-mono text-xs overflow-auto max-h-64">
                      <pre>{JSON.stringify(record.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border-t border-dashed border-gray-300 pt-6 mt-6">
      <h3 className="text-lg font-semibold mb-4 text-[#2c5aa0]">History</h3>
      {renderEyeHistory('OD', odRecords)}
      {renderEyeHistory('OS', osRecords)}
    </div>
  );
}

export default HistoryTimeline;

